import { buildAccessTokenProvider } from '@app/api/token';
export const resolveAccessToken = buildAccessTokenProvider();

type HeadersMap = Record<string, string>;

const isHeadersIterable = (value: unknown): value is {
  forEach: (callback: (value: unknown, key: unknown) => void) => void;
} => {
  return Boolean(value && typeof (value as any).forEach === 'function');
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  if (Array.isArray(value)) {
    return false;
  }
  if (isHeadersIterable(value)) {
    return false;
  }
  return true;
};

type TokenResolver = (name?: string, scopes?: string[]) => string | Promise<string | undefined>;

export const cloneHeadersWithAuth = async (
  input: unknown,
  tokenResolver: TokenResolver = resolveAccessToken
): Promise<HeadersMap> => {
  const headers: HeadersMap = {};

  if (isHeadersIterable(input)) {
    input.forEach((value: unknown, key: unknown) => {
      headers[String(key)] = String(value);
    });
  } else if (Array.isArray(input)) {
    for (const entry of input) {
      if (Array.isArray(entry) && entry.length >= 2) {
        const [key, value] = entry;
        headers[String(key)] = String(value);
      }
    }
  } else if (isPlainObject(input)) {
    for (const [key, value] of Object.entries(input)) {
      if (typeof value === 'undefined' || value === null) {
        continue;
      }
      headers[String(key)] = String(value);
    }
  }

  if (!('Authorization' in headers) && !('authorization' in headers)) {
    const token = await Promise.resolve(tokenResolver());
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return headers;
};

export const resolveBasePath = (apiBaseUrl: string): string => {
  if (/^https?:\/\//i.test(apiBaseUrl)) {
    return apiBaseUrl.replace(/\/+$/, '');
  }

  const normalized = apiBaseUrl.startsWith('/') ? apiBaseUrl : `/${apiBaseUrl}`;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${normalized}`.replace(/\/+$/, '');
};

export const buildOrgUrl = (basePath: string, orgId: string, resource: string = ''): string => {
  const trimmedBase = basePath.replace(/\/+$/, '');
  const trimmedResource = resource.replace(/^\/+/, '');
  const encodedOrg = encodeURIComponent(orgId);
  return trimmedResource.length > 0 ? `${trimmedBase}/orgs/${encodedOrg}/${trimmedResource}` : `${trimmedBase}/orgs/${encodedOrg}`;
};

export class ApiResponseError extends Error {
  response: Response;

  constructor(response: Response, message: string) {
    super(message);
    this.response = response;
  }
}
